import { Link } from "react-router-dom";
import {
  ArrowRight, Globe2, FileText, UserCheck, Wallet, ReceiptText,
  CheckCircle2, MapPin, Rocket, Repeat, Users, ShieldCheck,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import FAQ from "@/components/FAQ";

const HERO_IMG = "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80";

const audience = [
  "International companies expanding into Canada without a legal entity",
  "US companies hiring remote Canadian talent compliantly",
  "Canadian companies hiring in provinces where they don't have a registered presence",
  "Companies converting contractors to employees without employment-law risk",
  "Startups that need to hire fast before their legal entity is fully established",
];

const steps = [
  { icon: UserCheck, title: "You find the talent (or we do)", body: "You identify the candidate you want to hire — or we source them for you through our staffing network. You negotiate role, compensation, and start date directly." },
  { icon: FileText, title: "We onboard the employee", body: "Ashren prepares a compliant Canadian employment contract, handles all provincial onboarding requirements, and enrolls the employee in our payroll and benefits systems." },
  { icon: Users, title: "You direct the work", body: "The employee works for you — your projects, your tools, your processes. Ashren stays in the background managing payroll, remittances, T4s, and employment compliance." },
  { icon: Wallet, title: "We handle all employer obligations", body: "Payroll processing, statutory deductions (CPP, EI, income tax), vacation pay, statutory holidays, WorkSafeBC / WCB coverage, and year-end T4 filing — all handled by Ashren." },
  { icon: ReceiptText, title: "You get one simple monthly invoice", body: "We bill you a single consolidated invoice covering employee compensation plus Ashren's EOR management fee. No surprises, no compliance risk." },
];

const included = [
  "Compliant Canadian employment contract (provincial-specific)",
  "Payroll processing in all 10 provinces and 3 territories",
  "CPP, EI, and income-tax remittances to CRA",
  "T4 preparation and year-end filing",
  "Statutory holiday and vacation-pay tracking",
  "WorkSafeBC / WCB coverage in applicable provinces",
  "Benefits administration (health, dental, vision — optional)",
  "Employee onboarding and offboarding management",
  "HR advisory — provincial employment-standards compliance",
  "Bilingual employment documentation (EN/FR for Quebec)",
  "Immigration support coordination (work-permit holders)",
  "PIPEDA-compliant employee-data management",
  "Dedicated EOR account manager for every client",
];

const provinces = [
  "British Columbia", "Alberta", "Saskatchewan", "Manitoba",
  "Ontario", "Quebec", "New Brunswick", "Nova Scotia",
  "Prince Edward Island", "Newfoundland & Labrador",
  "Yukon", "Northwest Territories", "Nunavut",
];

const useCases = [
  { icon: Globe2, color: "var(--accent-teal)", title: "Cross-border expansion", tag: "US company hiring in Canada", body: "Your San Francisco startup wants to hire a remote engineering team in Vancouver but doesn't have a Canadian entity. Ashren EOR has your first Canadian employee onboarded and legally compliant in under 5 business days." },
  { icon: Rocket, color: "var(--accent-purple)", title: "Market entry without entity setup", tag: "International company", body: "Setting up a Canadian corporation takes 4–8 weeks and requires provincial registrations, a registered agent, and a local banking relationship. With Ashren EOR you can hire your Canadian team today and set up your entity on your own timeline." },
  { icon: Repeat, color: "var(--accent-mint)", title: "Risk-free conversion", tag: "Contractor to employee", body: "You've been using a Canadian contractor but you're concerned about misclassification risk or the contractor wants employment status. Ashren EOR converts them to a compliant employee without disrupting the working relationship." },
  { icon: MapPin, color: "var(--accent-amber)", title: "Hiring across provinces", tag: "Multi-province payroll", body: "You have employees in BC, Ontario, and Quebec — each with different employment standards. Ashren EOR manages the compliance complexity across all three provinces under one consolidated invoice." },
];

const differentiators = [
  { title: "100% Canadian", body: "We are a Canadian company managing Canadian employees under Canadian law. No cross-border compliance risk, no currency-conversion complexity, no offshore data handling. Everything stays in Canada." },
  { title: "Technology + Healthcare EOR Specialists", body: "Most EOR providers are generalists. Ashren specialises in Technology and Healthcare workers — we understand credential verification, professional licensing, and the specific compliance requirements of these sectors." },
  { title: "Bilingual EN/FR", body: "We issue compliant French-language employment documentation for Quebec employees — a legal requirement most EOR providers miss or outsource." },
  { title: "Fast Onboarding", body: "First employee onboarded within 3–5 business days of signed agreement. We don't make your new hire wait three weeks while paperwork clears." },
  { title: "Transparent Fee Structure", body: "Ashren EOR is priced as a flat percentage of gross employee salary — no hidden setup fees, no per-payroll charges, no annual minimums buried in the fine print." },
  { title: "Integrated Staffing + EOR", body: "If you found your talent through Ashren's staffing division, the transition to EOR is seamless — same account manager, same relationship, no handoff friction." },
];

const priceItems = [
  { title: "EOR Management Fee", value: "8% – 12%", per: "of gross monthly salary", note: "Includes payroll processing, remittances, T4, compliance, HR advisory, and account management.", accent: "var(--accent-teal)" },
  { title: "One-Time Setup", value: "$500", per: "per employee", note: "Includes contract preparation, onboarding, and system setup.", accent: "var(--accent-purple)" },
  { title: "Benefits Package (Optional)", value: "From $180", per: "per employee / month", note: "Extended health, dental, and vision — group-plan rates.", accent: "var(--accent-mint)" },
];

const faqs = [
  { q: "Do I need a Canadian legal entity to use Ashren EOR?", a: "No. That is the primary purpose of EOR. Ashren is the legal employer — you do not need a Canadian corporation, provincial registration, or local bank account." },
  { q: "How is EOR different from a staffing agency?", a: "A staffing agency finds and supplies workers. An EOR legally employs workers that you have already chosen — handling all the compliance, payroll, and HR obligations on your behalf. Ashren offers both, and the two services work together seamlessly." },
  { q: "Can you employ workers in Quebec?", a: "Yes. We issue bilingual French-English employment contracts compliant with Quebec's distinct employment standards and Act Respecting Labour Standards. We also manage Quebec Pension Plan (QPP) remittances separately from CPP." },
  { q: "What happens if we want to terminate an employee?", a: "Ashren manages the termination process in full compliance with the applicable provincial employment standards — including proper notice periods, severance calculations, and ROE filing. We guide you through every step." },
  { q: "Can you EOR work-permit holders?", a: "Yes, with conditions. We can employ workers who hold valid Canadian work authorization — including open work permits, employer-specific work permits (where Ashren is the employer of record on the permit), and permanent residents. We coordinate with immigration counsel where required." },
  { q: "How long does it take to onboard the first employee?", a: "3–5 business days from signed EOR agreement to first payroll cycle. Employees can typically start working within that window." },
  { q: "What's the minimum number of employees?", a: "There is no minimum. We support single-employee EOR engagements as well as large multi-province teams." },
];

export default function EOR() {
  return (
    <div data-testid="eor-page">
      <PageHero
        eyebrow="Employer of Record · Canada"
        title={<>Hire anyone in Canada. <span className="italic" style={{ color: "var(--accent-mint)" }}>We handle the rest.</span></>}
        subtitle="Ashren's Employer of Record service allows companies — Canadian or international — to hire and pay workers in Canada without establishing a local legal entity. We become the legal employer on paper. You direct the work. We handle payroll, taxes, benefits, compliance, and provincial employment law — in every province."
        image={HERO_IMG}
        imageAlt="EOR Canadian professionals"
        testId="eor-hero"
      />

      {/* Trust bar */}
      <section className="bg-white border-b border-[color:var(--border)] py-4" data-testid="eor-trust-bar">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-sm text-[color:var(--muted)]">
          {["BC Licensed Agency", "Ontario THA Licensed", "PIPEDA Compliant", "Bilingual EN/FR", "Pan-Canadian Coverage"].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <CheckCircle2 size={14} style={{ color: "var(--accent-mint)" }} />
              {t}
            </div>
          ))}
        </div>
      </section>

      {/* What is EOR */}
      <section className="py-20 md:py-24" data-testid="eor-what">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">Definition</div>
            <h2 className="display-lg">What is an Employer of Record?</h2>
          </div>
          <div className="md:col-span-7 text-[color:var(--muted)] leading-relaxed text-lg space-y-4">
            <p>
              An Employer of Record (EOR) is a company that legally employs workers on behalf of
              another business. When you use Ashren as your EOR in Canada, we become the worker&apos;s
              legal employer for payroll, tax, and compliance purposes — while you maintain full
              day-to-day management and direction of their work.
            </p>
            <p>
              This means you can hire talent anywhere in Canada — from a software engineer in
              Vancouver to a data scientist in Toronto to a healthcare professional in Halifax —
              without needing a Canadian corporation, a provincial business registration, or an HR
              and payroll infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Who uses EOR */}
      <section className="py-16 md:py-20 bg-white border-y border-[color:var(--border)]" data-testid="eor-audience">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-8">
            <div className="eyebrow mb-3" style={{ color: "var(--accent-teal)" }}>Who uses EOR</div>
            <h2 className="display-lg">Built for teams hiring across borders and provinces.</h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4">
            {audience.map((a) => (
              <li key={a} className="flex items-start gap-3 text-[color:var(--primary-dark)]">
                <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-teal)" }} />
                <span className="text-[15px] leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works — 5 steps */}
      <section className="py-20 md:py-24" data-testid="eor-how">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">How it works</div>
            <h2 className="display-lg">Five clean steps. Zero compliance risk.</h2>
          </div>
          <div className="space-y-4">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="card grid md:grid-cols-12 gap-6 items-start" data-testid={`eor-step-${i}`}>
                  <div className="md:col-span-1">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display text-lg" style={{ background: "var(--primary-dark)", color: "#fff" }}>
                      {i + 1}
                    </div>
                  </div>
                  <div className="md:col-span-1 hidden md:block">
                    <Icon size={22} style={{ color: "var(--accent-teal)" }} />
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="font-display text-xl mb-2">{s.title}</h3>
                    <p className="text-[color:var(--muted)] leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="eor-included">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3" style={{ color: "var(--accent-mint)" }}>What&apos;s included</div>
            <h2 className="display-lg">Every employer obligation — covered.</h2>
          </div>
          <div className="md:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {included.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[color:var(--primary-dark)]">
                  <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "var(--accent-mint)" }} />
                  <span className="text-sm leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Province coverage */}
      <section className="py-20 md:py-24" data-testid="eor-provinces">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="eyebrow mb-3">Pan-Canadian coverage</div>
              <h2 className="display-lg">Every province. Every territory.</h2>
              <p className="mt-4 text-[color:var(--muted)] leading-relaxed">
                Each province has different employment standards, notice periods, vacation
                entitlements, and WCB rates. Ashren manages every variation so you don&apos;t have to.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {provinces.map((p) => (
                  <div key={p} className="flex items-center gap-2 p-3 rounded-lg border border-[color:var(--border)] bg-white" data-testid={`eor-province-${p}`}>
                    <MapPin size={14} style={{ color: "var(--accent-teal)" }} />
                    <span className="text-sm">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="eor-use-cases">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">Common scenarios</div>
            <h2 className="display-lg">When teams turn to Ashren EOR.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {useCases.map((uc, i) => {
              const Icon = uc.icon;
              return (
                <div key={uc.title} className="practice-card h-full" style={{ "--accent": uc.color }} data-testid={`eor-use-case-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: `${uc.color}22`, color: uc.color }}>
                    <Icon size={22} />
                  </div>
                  <div className="eyebrow mb-2" style={{ color: uc.color }}>{uc.tag}</div>
                  <h3 className="font-display text-xl mb-3">{uc.title}</h3>
                  <p className="text-sm text-[color:var(--muted)] leading-relaxed">{uc.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Ashren */}
      <section className="py-20 md:py-24" data-testid="eor-why">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">Why Ashren</div>
            <h2 className="display-lg">Six reasons global teams pick us for EOR in Canada.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentiators.map((d, i) => (
              <div key={d.title} className="card h-full" data-testid={`eor-diff-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold" style={{ background: "rgba(46,204,155,0.14)", color: "var(--accent-mint)" }}>
                    {i + 1}
                  </div>
                  <h3 className="font-display text-lg">{d.title}</h3>
                </div>
                <p className="text-sm text-[color:var(--muted)] leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-24 bg-white border-y border-[color:var(--border)]" data-testid="eor-pricing">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3" style={{ color: "var(--accent-teal)" }}>Simple EOR pricing</div>
            <h2 className="display-lg">Priced as a flat percentage of gross salary. That&apos;s it.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {priceItems.map((p) => (
              <div key={p.title} className="card h-full flex flex-col" data-testid={`eor-price-${p.title}`}>
                <div className="eyebrow mb-3" style={{ color: p.accent }}>{p.title}</div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="font-display text-3xl md:text-4xl text-[color:var(--primary-dark)]">{p.value}</span>
                  {p.per && <span className="text-[color:var(--muted)] text-sm">{p.per}</span>}
                </div>
                <p className="text-sm text-[color:var(--muted)] leading-relaxed">{p.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-[color:var(--muted)] text-center max-w-2xl mx-auto">
            Volume pricing available for 5+ employees. <Link to="/for-employers" className="underline">Contact us</Link> for a custom quote.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24" data-testid="eor-faq-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3">Common questions</div>
            <h2 className="display-lg">EOR, answered.</h2>
          </div>
          <div className="md:col-span-8">
            <FAQ items={faqs} testId="eor-faq" />
          </div>
        </div>
      </section>

      {/* Cross-sell */}
      <section className="py-14 bg-white border-y border-[color:var(--border)]" data-testid="eor-cross-sell">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <div className="eyebrow mb-2" style={{ color: "var(--accent-purple)" }}>Also from Ashren</div>
            <h3 className="font-display text-2xl md:text-3xl mb-2">Need help finding the talent first?</h3>
            <p className="text-[color:var(--muted)] leading-relaxed">
              Ashren&apos;s Technology, Healthcare, and General Staffing divisions source and screen
              candidates before handing them off to EOR — one partner for the full hiring lifecycle.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/technology-staffing" className="btn-secondary" data-testid="eor-cross-sell-tech">
              Explore staffing <ArrowRight size={16} />
            </Link>
            <Link to="/rpo" className="btn-secondary" data-testid="eor-cross-sell-rpo">
              See RPO <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTABand
        title="Ready to hire in Canada without the complexity?"
        subtitle="Get your first Canadian employee onboarded and legally compliant in under 5 business days. Talk to our EOR team today."
        primaryLabel="Get EOR pricing"
        primaryTo="/for-employers"
        secondaryLabel="Book a free consultation"
        secondaryTo="/contact"
      />
    </div>
  );
}
